import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const loadPdfImage = (source: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const image = new Image();
    const timeout = setTimeout(() => finish(new Error('Image load timed out.')), 10000);
    const finish = (error?: Error) => {
        clearTimeout(timeout);
        image.onload = null;
        image.onerror = null;
        error ? reject(error) : resolve(image);
    };
    image.crossOrigin = 'anonymous';
    image.onload = () => finish();
    image.onerror = () => finish(new Error('Could not load PDF image.'));
    image.src = source;
});

// Wait for fonts and chart animations to settle before cloning the dashboard.
export const waitForDashboardPaint = async (content: HTMLElement): Promise<void> => {
    const fonts = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts;
    if (fonts) await fonts.ready;
    await new Promise<void>((resolve, reject) => {
        let quietTimer: ReturnType<typeof setTimeout>;
        const finish = (error?: Error) => {
            clearTimeout(quietTimer);
            clearTimeout(timeout);
            observer.disconnect();
            error ? reject(error) : resolve();
        };
        const settle = () => {
            clearTimeout(quietTimer);
            quietTimer = setTimeout(() => finish(), 350);
        };
        const observer = new MutationObserver(settle);
        const timeout = setTimeout(() => finish(new Error('Dashboard is still rendering. Please try again.')), 15000);
        observer.observe(content, { attributes: true, childList: true, characterData: true, subtree: true });
        settle();
    });
};

export const captureDashboardPdf = async (content: HTMLElement, onProgress: (page: number, total: number) => void) => {
    await waitForDashboardPaint(content);
    const width = Math.ceil(Math.max(content.clientWidth, content.scrollWidth));
    const height = Math.ceil(Math.max(content.clientHeight, content.scrollHeight));
    if (!width || !height) throw new Error('Dashboard has no content to export.');

    // Render one landscape page at a time to avoid browser canvas size/memory limits.
    const scale = Math.min(2, 4096 / width);
    const pageHeight = Math.max(1, Math.floor(width * 210 / 297));
    const total = Math.ceil(height / pageHeight);
    const pdfWidth = Math.floor(width * scale);
    const pdfHeight = Math.floor(pageHeight * scale);
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [pdfWidth, pdfHeight] });
    for (let page = 0; page < total; page++) {
        onProgress(page + 1, total);
        // Yield so the progress indicator can paint between pages.
        await new Promise(resolve => setTimeout(resolve, 0));
        const canvas = await html2canvas(content, {
            scale,
            width,
            height: Math.min(pageHeight, height - page * pageHeight),
            y: page * pageHeight,
            useCORS: true,
            logging: false,
            backgroundColor: '#f8f9fa',
            ignoreElements: element => element.hasAttribute('data-share-button') || element.hasAttribute('data-view-export-button'),
            onclone: (_document, clone) => {
                // Preserve chart dimensions while exposing content below the scroll viewport.
                clone.style.overflow = 'visible';
                clone.scrollTop = 0;
                clone.scrollLeft = 0;
            }
        });
        if (!canvas.width || !canvas.height) throw new Error('Could not capture dashboard.');
        if (page > 0) pdf.addPage([pdfWidth, pdfHeight], 'landscape');
        pdf.addImage(canvas, 'PNG', 0, 0, pdfWidth, canvas.height, undefined, 'FAST');
        canvas.width = 0;
        canvas.height = 0;
    }
    pdf.setPage(1);
    return { pdf, width: pdfWidth };
};
